import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, RefreshCw, AlertCircle } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { AdminTicket, TicketCounts, TicketStatus } from '@/types';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_TABS: { label: string; value: TicketStatus | 'all' }[] = [
  { label: 'All',      value: 'all' },
  { label: 'Open',     value: 'open' },
  { label: 'Pending',  value: 'pending' },
  { label: 'Closed',   value: 'closed' },
  { label: 'Resolved', value: 'resolved' },
];

const STATUS_BADGE: Record<string, string> = {
  open:     'bg-blue-100 text-blue-700',
  pending:  'bg-yellow-100 text-yellow-700',
  closed:   'bg-gray-100 text-gray-600',
  resolved: 'bg-green-100 text-green-700',
};

function relativeTime(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Tickets() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab]   = useState<TicketStatus | 'all'>('all');
  const [tickets, setTickets]       = useState<AdminTicket[]>([]);
  const [counts, setCounts]         = useState<TicketCounts | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  async function load(status: TicketStatus | 'all') {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listAdminTickets(status === 'all' ? undefined : status);
      setTickets(res.tickets);
      setCounts(res.counts);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(activeTab); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  function switchTab(tab: TicketStatus | 'all') {
    setActiveTab(tab);
  }

  return (
    <div className="max-w-6xl">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Support Tickets</h2>
          <p className="text-gray-500 mt-1 text-sm">Manage and respond to customer requests</p>
        </div>
        <button
          onClick={() => load(activeTab)}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* ── Summary cards ── */}
      {counts && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {([['Open', 'open', 'text-blue-600'], ['Pending', 'pending', 'text-yellow-600'], ['Resolved', 'resolved', 'text-green-600'], ['Closed', 'closed', 'text-gray-500']] as const).map(
            ([label, key, cls]) => (
              <button
                key={key}
                onClick={() => switchTab(key)}
                className={`bg-white rounded-xl border p-4 text-left transition-colors hover:border-brand-300 ${activeTab === key ? 'border-brand-400 ring-1 ring-brand-300' : 'border-gray-200'}`}
              >
                <div className={`text-2xl font-bold ${cls}`}>{counts[key]}</div>
                <div className="text-xs text-gray-500 mt-1">{label}</div>
              </button>
            )
          )}
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => switchTab(tab.value)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.value
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
            {tab.value !== 'all' && counts && (
              <span className="ml-1.5 text-xs text-gray-400">({counts[tab.value]})</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg text-red-800 text-sm mb-4">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className="text-sm text-gray-500 py-16 text-center">Loading…</div>
      )}

      {/* ── Empty state ── */}
      {!loading && !error && tickets.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <Inbox size={40} className="text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-900 mb-1">No tickets</p>
          <p className="text-sm text-gray-500">
            {activeTab === 'all'
              ? 'No support tickets have been submitted yet.'
              : `No ${activeTab} tickets.`}
          </p>
        </div>
      )}

      {/* ── Table ── */}
      {!loading && tickets.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 text-left w-10">#</th>
                <th className="px-5 py-3 text-left">Subject</th>
                <th className="px-5 py-3 text-left">Submitted by</th>
                <th className="px-5 py-3 text-left">Tenant</th>
                <th className="px-5 py-3 text-center">Status</th>
                <th className="px-5 py-3 text-right">Replies</th>
                <th className="px-5 py-3 text-right">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tickets.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/tickets/${t.id}`)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-4 text-gray-400 font-mono text-xs">{t.id}</td>
                  <td className="px-5 py-4">
                    <div className="font-medium text-gray-900 truncate max-w-xs">{t.subject}</div>
                    {t.user_reply_count > 0 && t.agent_reply_count === 0 && (
                      <div className="text-xs text-orange-500 mt-0.5 font-medium">Awaiting reply</div>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <div className="text-gray-900">{t.user_name || '—'}</div>
                    {t.user_email && (
                      <div className="text-xs text-gray-400 mt-0.5">{t.user_email}</div>
                    )}
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-gray-500">{t.tenant_id}</td>
                  <td className="px-5 py-4 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[t.status] ?? STATUS_BADGE.open}`}>
                      {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right text-gray-600">{t.reply_count}</td>
                  <td className="px-5 py-4 text-right text-gray-400 text-xs whitespace-nowrap">
                    {relativeTime(t.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
